// FilterBar — search + faceted filters above a DataTable.
//
// Pairs with useTableState, so everything it changes lands in the URL and the
// view stays linkable. The search input is intentionally NOT the first input in
// the DOM of a page — see the note on SearchInput below.

import { cn } from '@fajrarabi/ui';
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';

import { Icon } from './Icon';

export interface FilterOption {
  value: string;
  label: string;
  /** Optional count shown after the label, e.g. "Paid 128". */
  count?: number;
}

export interface FilterBarProps {
  /** Controlled search text (raw, not debounced — debounce at the query). */
  query?: string;
  onQueryChange?: (value: string) => void;
  searchPlaceholder?: string;
  /** Faceted selects. */
  filters?: ReadonlyArray<{
    key: string;
    label: string;
    value: string;
    options: readonly FilterOption[];
  }>;
  onFilterChange?: (key: string, value: string | null) => void;
  onClear?: () => void;
  isFiltered?: boolean;
  /** Extra controls: date range, export, view switcher. */
  children?: ReactNode;
  className?: string;
}

export function FilterBar({
  query,
  onQueryChange,
  searchPlaceholder = 'Search…',
  filters = [],
  onFilterChange,
  onClear,
  isFiltered = false,
  children,
  className,
}: FilterBarProps) {
  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      {onQueryChange && (
        <SearchInput value={query ?? ''} onChange={onQueryChange} placeholder={searchPlaceholder} />
      )}

      {filters.map((f) => (
        <FilterSelect
          key={f.key}
          label={f.label}
          value={f.value}
          options={f.options}
          onChange={(v) => onFilterChange?.(f.key, v)}
        />
      ))}

      {children}

      {isFiltered && onClear && (
        <button
          type="button"
          onClick={onClear}
          className="rounded-lg px-2.5 py-2 text-sm font-medium text-admin-500 transition-colors hover:bg-admin-100 hover:text-admin-700"
        >
          Clear all
        </button>
      )}
    </div>
  );
}

/**
 * The table's own search box.
 *
 * ⚠️ AUTOMATION TRAP (documented in next_session.md): the admin shell renders a
 * GLOBAL search input in the header, before any page content, so
 * `document.querySelector('input')` finds THAT, not this one. Three separate
 * "bugs" were traced to it. This input therefore carries an explicit id/label
 * pair so it can be resolved unambiguously by `<label for>`.
 */
export function SearchInput({
  value,
  onChange,
  placeholder = 'Search…',
  className,
  id,
  label = 'Search this list',
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  id?: string;
  label?: string;
}) {
  const generatedId = useId();
  const inputId = id ?? `table-search-${generatedId}`;

  return (
    <div className={cn('relative', className)}>
      <label htmlFor={inputId} className="sr-only">
        {label}
      </label>
      <span className="pointer-events-none absolute inset-y-0 start-0 grid w-9 place-items-center text-admin-400">
        <Icon name="search" className="h-4 w-4" />
      </span>
      <input
        id={inputId}
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-9 w-56 rounded-lg border border-admin-200 bg-white ps-9 pe-8 text-sm text-admin-800 placeholder:text-admin-400 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label="Clear search"
          className="absolute inset-y-0 end-0 grid w-8 place-items-center text-admin-400 hover:text-admin-600"
        >
          <Icon name="close" className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

/** A faceted filter. Native <select> — keyboard and screen-reader support for free. */
export function FilterSelect({
  label,
  value,
  options,
  onChange,
  className,
}: {
  label: string;
  value: string;
  options: readonly FilterOption[];
  onChange: (value: string | null) => void;
  className?: string;
}) {
  const id = useId();
  const active = value !== '';

  return (
    <div className={cn('relative', className)}>
      <label htmlFor={id} className="sr-only">
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
        className={cn(
          'h-9 appearance-none rounded-lg border ps-3 pe-8 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500/20',
          active
            ? 'border-brand-500 bg-brand-25 font-medium text-brand-700'
            : 'border-admin-200 bg-white text-admin-700',
        )}
      >
        <option value="">{label}: All</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
            {o.count != null ? ` (${o.count})` : ''}
          </option>
        ))}
      </select>
      <span className="pointer-events-none absolute inset-y-0 end-0 grid w-8 place-items-center text-admin-400">
        <Icon name="chevronDown" className="h-4 w-4" />
      </span>
    </div>
  );
}

/**
 * Date-range control with the presets staff actually use, plus custom.
 * The chosen range is the caller's to persist (usually via useTableState).
 */
export type DateRangePreset = '7d' | '30d' | '90d' | 'mtd' | 'ytd' | 'all' | 'custom';

export interface DateRange {
  preset: DateRangePreset;
  from: string | null;
  to: string | null;
}

const PRESET_LABEL: Record<DateRangePreset, string> = {
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
  mtd: 'Month to date',
  ytd: 'Year to date',
  all: 'All time',
  custom: 'Custom',
};

/** Resolve a preset to concrete ISO dates. `all` yields nulls (no bound). */
export function resolveDateRange(preset: DateRangePreset, now = new Date()): DateRange {
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  const daysAgo = (n: number) => {
    const d = new Date(now);
    d.setUTCDate(d.getUTCDate() - n);
    return d;
  };
  switch (preset) {
    case '7d':
      return { preset, from: iso(daysAgo(7)), to: iso(now) };
    case '30d':
      return { preset, from: iso(daysAgo(30)), to: iso(now) };
    case '90d':
      return { preset, from: iso(daysAgo(90)), to: iso(now) };
    case 'mtd':
      return {
        preset,
        from: iso(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1))),
        to: iso(now),
      };
    case 'ytd':
      return { preset, from: iso(new Date(Date.UTC(now.getUTCFullYear(), 0, 1))), to: iso(now) };
    default:
      return { preset, from: null, to: null };
  }
}

export function DateRangePicker({
  value,
  onChange,
  className,
}: {
  value: DateRange;
  onChange: (range: DateRange) => void;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const summary =
    value.preset === 'custom' && value.from
      ? `${value.from} → ${value.to ?? 'now'}`
      : PRESET_LABEL[value.preset];

  return (
    <div ref={wrapRef} className={cn('relative', className)}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-admin-200 bg-white px-3 text-sm font-medium text-admin-700 transition-colors hover:bg-admin-50"
      >
        <Icon name="clock" className="h-4 w-4 text-admin-400" />
        {summary}
        <Icon name="chevronDown" className="h-4 w-4 text-admin-400" />
      </button>

      {open && (
        <div className="absolute end-0 top-full z-30 mt-1 w-64 rounded-xl border border-admin-200 bg-white p-1.5 shadow-lg">
          {(['7d', '30d', '90d', 'mtd', 'ytd', 'all'] as const).map((preset) => (
            <button
              key={preset}
              type="button"
              onClick={() => {
                onChange(resolveDateRange(preset));
                setOpen(false);
              }}
              className={cn(
                'flex w-full items-center justify-between rounded-lg px-2.5 py-1.5 text-start text-sm transition-colors hover:bg-admin-50',
                value.preset === preset ? 'font-medium text-brand-700' : 'text-admin-700',
              )}
            >
              {PRESET_LABEL[preset]}
              {value.preset === preset && <Icon name="check" className="h-4 w-4" />}
            </button>
          ))}

          <div className="mt-1.5 border-t border-admin-100 pt-2">
            <p className="px-2.5 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-admin-400">
              Custom
            </p>
            <div className="flex items-center gap-1.5 px-2.5 pb-1">
              <input
                type="date"
                aria-label="From date"
                value={value.from ?? ''}
                onChange={(e) =>
                  onChange({ preset: 'custom', from: e.target.value || null, to: value.to })
                }
                className="h-8 w-full rounded-lg border border-admin-200 px-2 text-xs text-admin-800 focus:border-brand-500 focus:outline-none"
              />
              <span className="text-admin-400">→</span>
              <input
                type="date"
                aria-label="To date"
                value={value.to ?? ''}
                onChange={(e) =>
                  onChange({ preset: 'custom', from: value.from, to: e.target.value || null })
                }
                className="h-8 w-full rounded-lg border border-admin-200 px-2 text-xs text-admin-800 focus:border-brand-500 focus:outline-none"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
